import React from "react";
import {
  MantineProvider,
  Container,
  Alert,
  Loader,
  Flex,
  Box,
  Stack,
  Paper,
  Group,
  Text,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconFilter,
  IconFilterFilled,
  IconSearch,
  IconZoomFilled,
} from "@tabler/icons-react";
import { theme } from "./theme";
import "./App.css";
import { GpuListing } from "./types/GpuListing";
import { getRetailerColor, getRetailerDisplayName, getStockBadgeProps } from "./utils/styleHelpers";
import { useListings } from "./hooks/useListings";
import { useFilters } from "./hooks/useFilters";
import { PageHeader } from "./components/PageHeader";
import { PaginationControls } from "./components/PaginationControls";
import { FilterModal } from "./components/FilterModal";
import { MobileFilters } from "./components/MobileFilters";
import { ListingCard } from "./components/ListingCard";
import { PriceAlertModal } from "./components/PriceAlertModal";
import { ListingDetailModal } from "./components/ListingDetailModal";
import { SearchModal } from "./components/SearchModal";
import { PrivacyModal } from "./components/PrivacyModal";
import { useTheme } from "./contexts/ThemeContext";

function App() {
  const { gpuCard, theme: colorTheme } = useTheme();
  const themeColor = colorTheme === "green" ? "nvidiaGreen" : "amdRed";

  const isMockMode =
    new URLSearchParams(window.location.search).get("mock") === "true";

  const {
    listings,
    loading,
    error,
    currentPage,
    totalPages,
    totalListings,
    availableManufacturers,
    availableRetailers,
    lastUpdated,
    fetchListings,
    goToPage,
    refetchWithFilters,
  } = useListings(isMockMode);

  const {
    manufacturerFilter,
    setManufacturerFilter,
    retailerFilter,
    setRetailerFilter,
    stockFilter,
    setStockFilter,
    modelSearch,
    setModelSearch,
    productSearch,
    setProductSearch,
    sortField,
    sortDirection,
    handleSort,
    uniqueManufacturers,
    uniqueRetailers,
  } = useFilters({
    availableManufacturers,
    availableRetailers,
    gpuCard,
    onFilterChange: refetchWithFilters,
  });

  // Initial fetch on mount
  React.useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const [brandModalOpen, setBrandModalOpen] = React.useState(false);
  const [retailerModalOpen, setRetailerModalOpen] = React.useState(false);
  const [stockModalOpen, setStockModalOpen] = React.useState(false);
  const [modelSearchModalOpen, setModelSearchModalOpen] = React.useState(false);
  const [productSearchModalOpen, setProductSearchModalOpen] =
    React.useState(false);
  const [priceAlertModalOpen, setPriceAlertModalOpen] = React.useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = React.useState(false);
  const [selectedListing, setSelectedListing] =
    React.useState<GpuListing | null>(null);
  const [brandFilterHover, setBrandFilterHover] = React.useState(false);
  const [retailerFilterHover, setRetailerFilterHover] = React.useState(false);
  const [stockFilterHover, setStockFilterHover] = React.useState(false);
  const [modelSearchHover, setModelSearchHover] = React.useState(false);
  const [productSearchHover, setProductSearchHover] = React.useState(false);
  const [fadeOut, setFadeOut] = React.useState(false);
  const prevListingsRef = React.useRef(listings);

  const handleManufacturerToggle = (manufacturer: string, checked: boolean) => {
    if (checked) {
      setManufacturerFilter([...manufacturerFilter, manufacturer]);
    } else {
      setManufacturerFilter(
        manufacturerFilter.filter((m) => m !== manufacturer),
      );
    }
  };

  const handleRetailerToggle = (retailer: string, checked: boolean) => {
    if (checked) {
      setRetailerFilter([...retailerFilter, retailer]);
    } else {
      setRetailerFilter(retailerFilter.filter((r) => r !== retailer));
    }
  };

  const handleStockToggle = (status: string, checked: boolean) => {
    if (checked) {
      setStockFilter([...stockFilter, status]);
    } else {
      setStockFilter(stockFilter.filter((s) => s !== status));
    }
  };

  // Handle fade in/out when listings change
  React.useEffect(() => {
    if (prevListingsRef.current !== listings && listings.length > 0) {
      setFadeOut(true);
      const fadeOutTimer = setTimeout(() => {
        prevListingsRef.current = listings;
        setFadeOut(false);
      }, 150);
      return () => clearTimeout(fadeOutTimer);
    }
  }, [listings]);

  return (
    <MantineProvider theme={theme} defaultColorScheme="dark">
      <Box
        style={{
          minHeight: "100vh",
          background: `
          radial-gradient(circle at 25% 20%, var(--theme-overlay-14) 0%, transparent 40%),
          radial-gradient(circle at 30% 25%, var(--theme-overlay-10) 0%, transparent 40%),
          radial-gradient(circle at 70% 85%, var(--theme-overlay-12) 0%, transparent 50%),
          radial-gradient(circle at 60% 80%, var(--theme-overlay-08) 0%, transparent 50%),
          radial-gradient(circle at 50% 50%, var(--theme-overlay-06) 0%, transparent 70%),
          var(--theme-bg-main)
        `,
          backgroundAttachment: "fixed",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Container
          size="xl"
          py="xl"
          style={{ flex: 1, width: "100%", maxWidth: "1400px" }}
        >
          <Stack gap="xl">
            <PageHeader
              onAlertClick={() => setPriceAlertModalOpen(true)}
              onGpuChange={(card) => {
                fetchListings(card);
              }}
            />

            {loading && (
              <Flex
                justify="center"
                py="xl"
                style={{
                  opacity: fadeOut ? 0 : 1,
                  transition: "opacity 150ms ease-in-out",
                }}
              >
                <Loader size="lg" color={themeColor} />
              </Flex>
            )}

            {error && (
              <Alert color={themeColor} title="Error" radius="md">
                Something went wrong
              </Alert>
            )}

            {!loading && !error && listings.length === 0 && (
              <Alert color={themeColor} title="No listings found" radius="md"></Alert>
            )}

            {!loading && !error && listings.length > 0 && (
              <>
                <Stack gap="md" style={{ minHeight: "800px" }}>
                  <Box>
                    <Box className="mobile-filters">
                      <MobileFilters
                        onBrandClick={() => setBrandModalOpen(true)}
                        onStoreClick={() => setRetailerModalOpen(true)}
                        onStockClick={() => setStockModalOpen(true)}
                        onAlertClick={() => setPriceAlertModalOpen(true)}
                        manufacturerFilter={manufacturerFilter}
                        uniqueManufacturers={uniqueManufacturers}
                        retailerFilter={retailerFilter}
                        uniqueRetailers={uniqueRetailers}
                        stockFilter={stockFilter}
                      />
                    </Box>

                    <Paper
                      radius="lg"
                      p="md"
                      mb="xs"
                      className="desktop-table-header"
                      style={{
                        background: "rgba(8, 12, 10, 0.95)",
                        boxShadow: "0 4px 30px rgba(0, 0, 0, 0.2)",
                        border: "none",
                      }}
                    >
                      <Group gap="md" px="md" wrap="nowrap">
                        <Box style={{ width: "0.25rem" }} />
                        <Flex
                          style={{ flex: "0 0 200px" }}
                          gap="xs"
                          align="center"
                        >
                          <Flex gap="xs" align="center">
                            <Text
                              size="xs"
                              c="dimmed"
                              fw={600}
                              tt="uppercase"
                              className="hoverable-text"
                            >
                              Brand
                            </Text>
                            <Tooltip label="Filter" position="top">
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="gray"
                                onClick={() => setBrandModalOpen(true)}
                                onMouseEnter={() => setBrandFilterHover(true)}
                                onMouseLeave={() => setBrandFilterHover(false)}
                              >
                                {manufacturerFilter.length > 0 &&
                                manufacturerFilter.length <
                                uniqueManufacturers.length ? (
                                  <IconFilterFilled size={16} />
                                ) : brandFilterHover ? (
                                  <IconFilterFilled size={16} />
                                ) : (
                                  <IconFilter size={16} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Flex>
                          <Flex gap="xs" align="center">
                            <Text
                              size="xs"
                              c="dimmed"
                              fw={600}
                              tt="uppercase"
                              className="sortable-header"
                              onClick={() => handleSort("variant_name")}
                            >
                              Model{" "}
                              {sortField === "variant_name" &&
                                (sortDirection === "asc" ? "↑" : "↓")}
                            </Text>
                            <Tooltip label="Search" position="top">
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="gray"
                                onClick={() => setModelSearchModalOpen(true)}
                                onMouseEnter={() => setModelSearchHover(true)}
                                onMouseLeave={() => setModelSearchHover(false)}
                              >
                                {modelSearch ? (
                                  <IconZoomFilled size={16} />
                                ) : modelSearchHover ? (
                                  <IconZoomFilled size={16} />
                                ) : (
                                  <IconSearch size={16} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Flex>
                        </Flex>

                        <Flex
                          gap="xs"
                          style={{
                            flex: "1 1 auto",
                            minWidth: 0,
                            maxWidth: "400px",
                          }}
                          align="center"
                        >
                          <Text
                            size="xs"
                            c="dimmed"
                            fw={600}
                            tt="uppercase"
                            className="sortable-header"
                            onClick={() => handleSort("full_name")}
                          >
                            Product{" "}
                            {sortField === "full_name" &&
                              (sortDirection === "asc" ? "↑" : "↓")}
                          </Text>
                          <Tooltip label="Search" position="top">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="gray"
                              onClick={() => setProductSearchModalOpen(true)}
                              onMouseEnter={() => setProductSearchHover(true)}
                              onMouseLeave={() => setProductSearchHover(false)}
                            >
                              {productSearch ? (
                                <IconZoomFilled size={16} />
                              ) : productSearchHover ? (
                                <IconZoomFilled size={16} />
                              ) : (
                                <IconSearch size={16} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        </Flex>
                        <Group
                          gap="xs"
                          style={{
                            flex: "0 0 140px",
                            justifyContent: "center",
                          }}
                        >
                          <Text
                            size="xs"
                            c="dimmed"
                            fw={600}
                            tt="uppercase"
                            className="hoverable-text"
                          >
                            Store
                          </Text>
                          <Tooltip label="Filter" position="top">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="gray"
                              onClick={() => setRetailerModalOpen(true)}
                              onMouseEnter={() => setRetailerFilterHover(true)}
                              onMouseLeave={() => setRetailerFilterHover(false)}
                            >
                              {retailerFilter.length > 0 &&
                              retailerFilter.length <
                              uniqueRetailers.length ? (
                                <IconFilterFilled size={16} />
                              ) : retailerFilterHover ? (
                                <IconFilterFilled size={16} />
                              ) : (
                                <IconFilter size={16} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                        <Box style={{ flex: "0 0 60px" }} />
                        <Text
                          size="xs"
                          c="dimmed"
                          fw={600}
                          tt="uppercase"
                          className="sortable-header"
                          style={{
                            flex: "0 0 120px",
                          }}
                          onClick={() => handleSort("price")}
                        >
                          Price{" "}
                          {sortField === "price" &&
                            (sortDirection === "asc" ? "↑" : "↓")}
                        </Text>
                        <Group
                          gap="xs"
                          style={{
                            flex: "0 0 120px",
                            justifyContent: "center",
                          }}
                        >
                          <Text
                            size="xs"
                            c="dimmed"
                            fw={600}
                            tt="uppercase"
                            className="hoverable-text"
                          >
                            Stock
                          </Text>
                          <Tooltip label="Filter" position="top">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="gray"
                              onClick={() => setStockModalOpen(true)}
                              onMouseEnter={() => setStockFilterHover(true)}
                              onMouseLeave={() => setStockFilterHover(false)}
                            >
                              {stockFilter.length > 0 &&
                              stockFilter.length < 4 ? (
                                <IconFilterFilled size={16} />
                              ) : stockFilterHover ? (
                                <IconFilterFilled size={16} />
                              ) : (
                                <IconFilter size={16} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                        <Text
                          size="xs"
                          c="dimmed"
                          fw={600}
                          tt="uppercase"
                          className="hoverable-text"
                          style={{
                            flex: "0 0 80px",
                            textAlign: "center",
                          }}
                        >
                          Link
                        </Text>
                      </Group>
                    </Paper>

                    <Stack
                      gap="sm"
                      style={{
                        opacity: fadeOut ? 0 : 1,
                        transition: "opacity 150ms ease-in-out",
                      }}
                    >
                      {listings.map((listing) => (
                        <ListingCard
                          key={listing.id}
                          listing={listing}
                          onClick={() => setSelectedListing(listing)}
                        />
                      ))}
                    </Stack>

                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalListings={totalListings}
                      lastUpdated={lastUpdated}
                      onPageChange={goToPage}
                      onPrivacyClick={() => setPrivacyModalOpen(true)}
                    />
                  </Box>
                </Stack>
              </>
            )}
          </Stack>
        </Container>

        <Box style={{ marginTop: "auto", width: "100%" }}>
          <Text size="xs" c="dimmed" ta="center" mt="xl" pb="xl">
            <span style={{ fontStyle: "italic" }}>
              GPU o'Clock can make mistakes or break at any time.{" "}
              <span className="disclaimer-break" />
              <br className="disclaimer-second-break" />
              Use at your own discretion.
            </span>{" "}
            <a href="https://github.com/alenvall" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>❤️</a>
          </Text>
        </Box>
      </Box>

      <FilterModal
        opened={brandModalOpen}
        onClose={() => setBrandModalOpen(false)}
        options={uniqueManufacturers}
        selectedOptions={manufacturerFilter}
        onToggle={handleManufacturerToggle}
        onReset={() => setManufacturerFilter(uniqueManufacturers)}
      />

      <FilterModal
        opened={retailerModalOpen}
        onClose={() => setRetailerModalOpen(false)}
        options={uniqueRetailers}
        selectedOptions={retailerFilter}
        onToggle={handleRetailerToggle}
        onReset={() => setRetailerFilter(uniqueRetailers)}
        getBadgeProps={(retailer) => ({
          color: getRetailerColor(retailer),
          variant: "light",
          size: "lg",
          text: getRetailerDisplayName(retailer),
        })}
      />

      <FilterModal
        opened={stockModalOpen}
        onClose={() => setStockModalOpen(false)}
        options={["in_stock", "incoming", "out_of_stock", "unknown"]}
        selectedOptions={stockFilter}
        onToggle={handleStockToggle}
        onReset={() =>
          setStockFilter(["in_stock", "incoming", "out_of_stock", "unknown"])
        }
        getBadgeProps={(status) => {
          const props = getStockBadgeProps(status);
          return {
            color: props.color,
            variant: "outline",
            size: "lg",
            text: props.text,
            textColor: props.textColor,
          };
        }}
      />

      <ListingDetailModal
        listing={selectedListing}
        onClose={() => setSelectedListing(null)}
      />

      <SearchModal
        opened={modelSearchModalOpen}
        onClose={() => setModelSearchModalOpen(false)}
        onSearch={setModelSearch}
        placeholder="Search models..."
        initialValue={modelSearch}
      />

      <SearchModal
        opened={productSearchModalOpen}
        onClose={() => setProductSearchModalOpen(false)}
        onSearch={setProductSearch}
        placeholder="Search names..."
        initialValue={productSearch}
      />

      <PriceAlertModal
        opened={priceAlertModalOpen}
        onClose={() => setPriceAlertModalOpen(false)}
        lowestPrice={listings.length > 0 ? Math.min(...listings.map(l => l.price)) : 8000}
      />

      <PrivacyModal
        opened={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
      />
    </MantineProvider>
  );
}

export default App;
